import { Link, useLocation, useNavigate } from "react-router-dom";
import { coatIcon, logo, sideMenuIcon } from "../../../../assets/images";
import { MLawyerMenu } from "../../../../utils/appUtils/sideBarMenu";
import "./style.scss"
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import classNames from "classnames";
import * as api from "../../../../actions";
import { headerReducer } from "../../../../reducer/header.reducer";
import { ACCESSCONTROLMANAGEMENT, USERADMIN } from "../../../../apiServices/endpoints";
import { useQuery } from "@tanstack/react-query";
import { loaderReducer } from "../../../../reducer/loader.reducer";

const SIDEBAR_MENU_ORDER = [
    "Dashboard",
    "Advocate",
    "Requests",
    "Complaints",
    "Customer",
    "Support",
    "Consultations",
    "Transactions",
    "GstInvoice",
    "Penalty",
    "Coupon",
    "Config",
    "AccessController",
    "User",
];

const normalizeMenuName = (menuNameValue) => `${menuNameValue || ""}`?.replace(/[^a-zA-Z0-9]/g, "")?.toLowerCase();
const SIDEBAR_MENU_ORDER_MAP = SIDEBAR_MENU_ORDER?.reduce((menuOrderDetail, menuNameValue, menuIndex) => ({
    ...menuOrderDetail,
    [normalizeMenuName(menuNameValue)]: menuIndex,
}), {});

const SideBar = () => {
    const dispatch = useDispatch();
    const adminId = sessionStorage.getItem("userId");
    const adminRole = sessionStorage.getItem("role");
    const navigate = useNavigate();
    const location = useLocation();
    const [adminProfilePic, setAdminProfilePic] = useState();
    const [adminMenus, setAdminMenus] = useState(() => MLawyerMenu());
    const [currentMenu, setCurrentMenu] = useState(() => sessionStorage.getItem("currentMenu") || "Dashboard");
    const [adminDetails, setAdminDetails] = useState({ firstName: "", middleName: "", lastName: "" });
   
    const shouldDisplayMenu = (menuDetail) => {
        const accessLevels = menuDetail?.accessLevels;
        if (!Array.isArray(accessLevels) || accessLevels?.length === 0) {
            return false;
        }
        return !accessLevels?.some((accessLevel) => `${accessLevel || ""}`?.toUpperCase() === "HIDE");
    };

    const getAccessControlMenuItems = (accessControlResponse) => {
        const accessControlData = accessControlResponse?.result?.accessControlResponse;
        const menuDetails = Array.isArray(accessControlData)
            ? accessControlData?.[0]?.menus
            : accessControlData?.menus;
        if (!Array.isArray(menuDetails)) {
            return [];
        }
        return menuDetails
            ?.filter((menuDetail) => typeof menuDetail?.menuName === "string" && menuDetail?.menuName?.trim() && shouldDisplayMenu(menuDetail))
            ?.map((menuDetail, menuIndex) => ({
                MenuName: menuDetail?.menuName?.trim(),
                subMenu: [],
                accessLevels: menuDetail?.accessLevels || [],
                originalIndex: menuIndex,
            }))
            ?.sort((firstMenuDetail, secondMenuDetail) => {
                const firstMenuOrder = SIDEBAR_MENU_ORDER_MAP?.[normalizeMenuName(firstMenuDetail?.MenuName)] ?? Number.MAX_SAFE_INTEGER;
                const secondMenuOrder = SIDEBAR_MENU_ORDER_MAP?.[normalizeMenuName(secondMenuDetail?.MenuName)] ?? Number.MAX_SAFE_INTEGER;
                if (firstMenuOrder === secondMenuOrder) {
                    return (firstMenuDetail?.originalIndex ?? 0) - (secondMenuDetail?.originalIndex ?? 0);
                }
                return firstMenuOrder - secondMenuOrder;
            })
            ?.map(({ MenuName, subMenu, accessLevels }) => ({
                MenuName,
                subMenu,
                accessLevels,
            }));
    };

    const isEmptyAccessControlResponse = (accessControlResponse) => {
        const accessControlData = accessControlResponse?.result?.accessControlResponse;
        return Array.isArray(accessControlData) && accessControlData?.length === 0;
    };

    const getMenuRoutePath = (menuName) => {
        const normalizedMenuName = menuName?.trim() || "";
        if (normalizedMenuName === "Access Controller") {
            return "AccessController";
        }
        return normalizedMenuName;
    };

    const getMenuIcon = (menuName) => {
        const directIconKey = `icn_${menuName}`;
        const normalizedIconKey = `icn_${menuName?.replace(/[^a-zA-Z0-9]/g, "")}`;
        return sideMenuIcon?.[directIconKey] || sideMenuIcon?.[normalizedIconKey];
    };

    const getAdminById = (id) => api.get(`${USERADMIN}/${id}`);
    const getMenuItems = (role, userId) => {
        const queryParams = new URLSearchParams();
        if (role) {
            queryParams?.append("role", role);
        }
        if (userId) {
            queryParams?.append("userId", userId);
        }
        return api.get(`${ACCESSCONTROLMANAGEMENT}?${queryParams?.toString()}`);
    };

    const { isFetching: isFetchingAdminById } = useQuery(["FETCH_ADMIN_BY_ID", adminId], () => getAdminById(adminId), {
        onSuccess: (adminResponse) => {
            if (adminResponse?.statusCode === 200) {
                setAdminDetails(adminResponse?.result?.adminUserResponse);
                setAdminProfilePic(`${USERADMIN}/file${adminResponse?.result?.adminUserResponse?.profileUrl}`);
            }
        },
        enabled: Boolean(adminId),
        refetchOnWindowFocus: false,
    });

    const { isFetching: isFetchingAccessControls } = useQuery(["FETCH_ACCESS_CONTROL_MENU_ITEMS", adminRole, adminId], async () => {
        const accessControlResponse = await getMenuItems(adminRole, adminId);
        const isSuccessResponse = accessControlResponse?.statusCode === 200 || accessControlResponse?.status === "OK";
        if (isSuccessResponse && isEmptyAccessControlResponse(accessControlResponse) && adminRole) {
            return getMenuItems(adminRole);
        }
        return accessControlResponse;
    }, {
        onSuccess: (accessControlResponse) => {
            if (accessControlResponse?.statusCode === 200 || accessControlResponse?.status === "OK") {
                if (isEmptyAccessControlResponse(accessControlResponse)) {
                    setAdminMenus(MLawyerMenu());
                    return;
                }
                setAdminMenus(getAccessControlMenuItems(accessControlResponse));
                return;
            }
            setAdminMenus(MLawyerMenu());
        },
        enabled: Boolean(adminRole && adminId),
        retry: false,
        refetchOnWindowFocus: false,
    });

    const handleMenus = useCallback(
        (menuItem) => {
            const menuName = menuItem?.MenuName;
            dispatch(headerReducer(menuName));
            setCurrentMenu(menuName);
            sessionStorage.setItem("currentMenu", menuName);
            sessionStorage.setItem("headerTitle", menuName);
        }, [dispatch]);

    useEffect(() => {
        if (location?.pathname === "/") {
            const initialPath = adminMenus?.[0];
            if (initialPath) {
                navigate(`/${getMenuRoutePath(initialPath?.MenuName)}`);
                handleMenus(initialPath);
            }
            return;
        } else {
            const currentPath = decodeURIComponent(location?.pathname?.substring(1));
            const matchedMenu = adminMenus?.find((menuItem) => {
                const menuRoutePath = getMenuRoutePath(menuItem?.MenuName);
                return menuItem?.MenuName === currentPath || menuRoutePath === currentPath;
            });
            if (matchedMenu) {
                handleMenus(matchedMenu);
                return;
            }
            const initialPath = adminMenus?.[0];
            if (initialPath) {
                navigate(`/${getMenuRoutePath(initialPath?.MenuName)}`);
                handleMenus(initialPath);
            }
        }
    }, [location?.pathname, navigate, adminMenus, handleMenus]);

    useEffect(() => {
        const isLoading = isFetchingAdminById || isFetchingAccessControls;
        dispatch(loaderReducer(isLoading));
    }, [dispatch, isFetchingAccessControls, isFetchingAdminById]);

    return <>
        <div className="sidebar">
            <div className="sidebar-top-section">
                <div className="sidebar-header">
                    <img src={logo} alt="loading" style={{ width: "40px", height: "40px" }} />
                    <h3 style={{ color: "#fff", margin: 0, fontSize: "24px", fontWeight: "600" }}>MLawyer</h3>
                </div>
                <div className="adminName-section">
                    <div style={{ width: "20%", textAlign: "center" }}>
                        <img src={coatIcon || adminProfilePic} alt="loading" style={{ width: "30px", height: "30px", borderRadius: "50%", objectFit: "cover" }} />
                    </div>
                    <div className="adminName">
                        <h4 style={{ padding: 0, margin: 0, fontSize: "14px" }}>{adminDetails?.firstName} {adminDetails?.middleName} {adminDetails?.lastName}</h4>
                        <p style={{ padding: 0, margin: 0, fontSize: "13px" }}>Admin</p>
                    </div>
                </div>
                <div style={{ padding: "17px 0px 10px 0px", width: "100%", display: "flex", justifyContent: "center" }}>
                    <div className="line-section"></div>
                </div>
            </div>
            <div className="sidebar-menu-scroll">
                <nav>
                    <ul className="menuList">
                        {
                            adminMenus?.map((menus, index) => {
                                const subMenuPath = menus?.MenuName;
                                const routePath = getMenuRoutePath(subMenuPath);
                                return <li key={menus?.MenuName || index}>
                                        <Link
                                            className={classNames(
                                                "sidebar__list",
                                                {
                                                    "sidebar--active": currentMenu === menus?.MenuName,
                                                },
                                                "SubMenu",
                                            )}
                                            onClick={() => handleMenus(menus)}
                                            to={`/${routePath}`}
                                            style={{ textDecoration: 'none' }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', width: "100%" }} className="menushowing">
                                                <figure style={{ margin: 0, display: 'flex', alignItems: 'center', gap: "10px" }}>
                                                    <img src={getMenuIcon(menus?.MenuName)} alt={`${menus?.MenuName} icon`} style={{ width: "20px", height: "20px" }} />
                                                    <span style={{ whiteSpace: 'normal', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '150px' }}>
                                                        {menus?.MenuName?.replace(/([a-z])([A-Z])/g, '$1 $2')}
                                                    </span>
                                                </figure>
                                            </div>
                                        </Link>
                                    </li>
                            })
                        }
                    </ul>
                </nav>
            </div>
            <div className="sidebar-footer-section" style={{width:"100%"}}>
                <div style={{height:"1px",width:"100%",backgroundColor:"#fff"}}></div>
                <h3 style={{ fontSize: "16px",color:"#fff",textAlign:"center",paddingTop:"5px" }}>v0.0.3</h3>
            </div>
        </div>
    </>
};
export default SideBar